import React from "react";
import CheckIcon from "../../icons/CheckIcon.svg"
import { LightTooltip } from "../ui/tooltip";

const ProgressStepsV3 = ({
  steps = [],
  currentStep = 1,
  onStepClick,
  finalStatus,
  title = "Loan Application",
  collapsed = false,
  compact = false
}) => {
  const totalSteps = steps.length;
  // Clamp to [1, totalSteps] so a stale/out-of-range currentStep can't render
  // "Step 8 of 7" or overflow the bar. Falls back to 1 when there are no steps.
  const safeStep = Math.min(Math.max(1, currentStep), Math.max(1, totalSteps));
  // Guard against divide-by-zero when steps is empty (width would be Infinity%).
  const progressPct = totalSteps ? (safeStep / totalSteps) * 100 : 0;

  // Progress header (title + "Step X of Y" + bar) shared by the full and
  // compact renders.
  const progressHeader = (
    <div className="flex flex-col w-full" style={{ gap: "11.797px" }}>
      <div
        className="flex items-center justify-between text-white font-normal"
        style={{ fontSize: "13.763px", lineHeight: "19.662px" }}
      >
        <span>{title}</span>
        <span>
          Step {safeStep} of {totalSteps}
        </span>
      </div>
      <div
        className="w-full bg-white/20 overflow-hidden"
        style={{ height: "9.831px", borderRadius: "5.899px" }}
        role="progressbar"
        aria-valuenow={safeStep}
        aria-valuemin={1}
        aria-valuemax={totalSteps || 1}
        aria-valuetext={`Step ${safeStep} of ${totalSteps}`}
      >
        <div
          className="h-full bg-[#19ef8f] transition-all duration-500"
          style={{
            width: `${progressPct}%`,
            borderRadius: "11.797px"
          }}
        />
      </div>
    </div>
  );

  // Compact (mobile) variant: only the progress header, in its own navy strip.
  // Matches the Figma "Sidepanel/Apply Loan" mobile block.
  if (compact) {
    return (
      <div className="w-full bg-secondary-1 px-5 py-3">{progressHeader}</div>
    );
  }

  const stepIcon = (stepNumber, isCompleted, isActive) => (
    <div
      className="relative shrink-0 flex items-center justify-center"
      style={{ width: "31.459px", height: "31.459px" }}
    >
      {isCompleted && (
        <CheckIcon/>
      )}
      {isActive && (
        <>
          <div
            className="absolute inset-0 rounded-full"
            style={{ border: "1.5px solid rgba(255,255,255,0.45)" }}
          />
          <div
            className="flex items-center justify-center bg-white"
            style={{ width: "24.905px", height: "24.905px", borderRadius: "50%" }}
          >
            <span
              className="font-semibold leading-none select-none"
              style={{ color: "#343c76", fontSize: "15.73px" }}
            >
              {stepNumber}
            </span>
          </div>
        </>
      )}
      {!isCompleted && !isActive && (
        <div
          className="rounded-full"
          style={{
            width: "27.527px",
            height: "27.527px",
            border: "1.5px solid rgba(255,255,255,0.3)"
          }}
        />
      )}
    </div>
  );

  if (collapsed) {
    return (
      <div className="flex flex-col items-center w-full" style={{ gap: "23.594px" }}>
        {steps.map((_, index) => {
          const stepNumber = index + 1;
          const isCompleted = stepNumber < safeStep;
          const isActive = stepNumber === safeStep;
          const isClickable = isCompleted || isActive;

          const handleStepClick = () => {
            if (isClickable && !finalStatus && onStepClick) {
              onStepClick(stepNumber);
            }
          };

          return (
            <LightTooltip
              key={stepNumber}
              title={steps[index].label}
              side="right"
              align="center"
              variant="dark"
              sideOffset={8}
            >
              <button
                type="button"
                onClick={handleStepClick}
                className={`w-full flex items-center justify-center bg-transparent border-0 p-0 ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
                aria-label={steps[index].label}
              >
                {stepIcon(stepNumber, isCompleted, isActive)}
              </button>
            </LightTooltip>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full" style={{ gap: "23.594px" }}>
      {/* Progress header */}
      {progressHeader}

      {/* Steps */}
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const isCompleted = stepNumber < safeStep;
        const isActive = stepNumber === safeStep;
        const isClickable = isCompleted || isActive;

        const handleStepClick = () => {
          if (isClickable && !finalStatus && onStepClick) {
            onStepClick(stepNumber);
          }
        };

        return (
          <button
            key={stepNumber}
            type="button"
            onClick={handleStepClick}
            className={`flex items-center w-full text-left bg-transparent border-0 p-0 ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
            style={{ gap: "15.73px" }}
          >
            {stepIcon(stepNumber, isCompleted, isActive)}

            {/* Step label */}
            <span
              className="font-medium flex-1 min-w-0"
              style={{
                fontSize: "15.73px",
                lineHeight: "23.594px",
                color: isCompleted || isActive ? "white" : "#d9d9d9"
              }}
            >
              {step.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export { ProgressStepsV3 };
